import React, { useState, useEffect } from 'react';
import axios from '../config/api';
import { API_URL } from '../config/api';
import { MdAdd, MdEdit, MdDelete, MdSearch, MdFilterList, MdAccountBalance, MdLocationCity, MdHome, MdClose, MdCheck, MdWarning } from 'react-icons/md';

function AdminOffices() {
  const [offices, setOffices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterLevel, setFilterLevel] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingOffice, setEditingOffice] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    level: 'National',
    category: '',
    position_title: '',
    description: '',
    sort_order: 0
  });

  useEffect(() => {
    loadOffices();
  }, []);

  const loadOffices = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/offices`);
      // The endpoint returns offices grouped by level, flatten them
      const allOffices = [];
      const officesByLevel = response.data.offices_by_level || {};
      Object.keys(officesByLevel).forEach(level => {
        officesByLevel[level].forEach(office => {
          allOffices.push({ ...office, level });
        });
      });
      // Sort by level then sort_order
      allOffices.sort((a, b) => {
        const levelOrder = { 'National': 1, 'State': 2, 'Local': 3 };
        if (levelOrder[a.level] !== levelOrder[b.level]) {
          return levelOrder[a.level] - levelOrder[b.level];
        }
        return (a.sort_order || 0) - (b.sort_order || 0);
      });
      setOffices(allOffices);
    } catch (error) {
      console.error('Failed to load offices:', error);
      setError('Failed to load offices');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'sort_order' ? parseInt(value) || 0 : value
    }));
  };

  const resetForm = () => {
    setFormData({
      level: 'National',
      category: '',
      position_title: '',
      description: '',
      sort_order: 0
    });
    setEditingOffice(null);
    setError('');
  };

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (office) => {
    setFormData({
      level: office.level,
      category: office.category,
      position_title: office.position_title,
      description: office.description || '',
      sort_order: office.sort_order || 0
    });
    setEditingOffice(office);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validation
    if (!formData.category.trim()) {
      setError('Category is required');
      return;
    }
    if (!formData.position_title.trim()) {
      setError('Position title is required');
      return;
    }

    try {
      setSubmitting(true);
      
      if (editingOffice) {
        // Update existing office
        await axios.put(`${API_URL}/api/admin/offices/${editingOffice.office_id}`, formData);
        setSuccess('Office updated successfully');
      } else {
        // Create new office
        await axios.post(`${API_URL}/api/admin/offices`, formData);
        setSuccess('Office created successfully');
      }
      
      closeModal();
      loadOffices();
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to save office');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (office) => {
    if (!window.confirm(`Are you sure you want to delete "${office.position_title}"? This will remove it from the list of available offices.`)) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/admin/offices/${office.office_id}`);
      setSuccess(`"${office.position_title}" has been deleted`);
      loadOffices();
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to delete office');
    }
  };

  const getLevelIcon = (level) => {
    switch (level) {
      case 'National':
        return <MdAccountBalance className="text-blue-600" />;
      case 'State':
        return <MdLocationCity className="text-purple-600" />;
      case 'Local':
        return <MdHome className="text-green-600" />;
      default:
        return <MdAccountBalance className="text-gray-600" />;
    }
  };

  const getLevelColor = (level) => {
    switch (level) {
      case 'National':
        return 'bg-blue-100 text-blue-800';
      case 'State':
        return 'bg-purple-100 text-purple-800';
      case 'Local':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Filter and search offices
  const filteredOffices = offices.filter(office => {
    const matchesSearch = !searchQuery || 
      office.position_title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      office.category.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesLevel = filterLevel === 'all' || office.level === filterLevel;
    return matchesSearch && matchesLevel;
  });

  // Group by level for display
  const groupedOffices = filteredOffices.reduce((acc, office) => {
    if (!acc[office.level]) acc[office.level] = [];
    acc[office.level].push(office);
    return acc;
  }, {});

  return (
    <div className="max-w-6xl mx-auto px-4 py-8" data-testid="admin-offices-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 font-rubik">Office Management</h1>
          <p className="text-gray-600">Manage political offices for representative profiles</p>
        </div>
        <button
          onClick={openCreateModal}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
          data-testid="create-office-btn"
        >
          <MdAdd className="text-xl" />
          Add Office
        </button>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 flex items-center gap-2">
          <MdCheck className="text-lg" />
          {success}
          <button onClick={() => setSuccess('')} className="ml-auto"><MdClose /></button>
        </div>
      )}
      {error && !showModal && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-center gap-2">
          <MdWarning className="text-lg" />
          {error}
          <button onClick={() => setError('')} className="ml-auto"><MdClose /></button>
        </div>
      )}

      {/* Search and Filter */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-grow relative">
            <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xl" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search offices by title or category..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
              data-testid="office-search-input"
            />
          </div>
          
          {/* Level Filter */}
          <div className="flex items-center gap-2">
            <MdFilterList className="text-gray-400 text-xl" />
            <select
              value={filterLevel}
              onChange={(e) => setFilterLevel(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
              data-testid="office-level-filter"
            >
              <option value="all">All Levels</option>
              <option value="National">National</option>
              <option value="State">State</option>
              <option value="Local">Local</option>
            </select>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        {['National', 'State', 'Local'].map(level => {
          const count = offices.filter(o => o.level === level).length;
          return (
            <div key={level} className={`${getLevelColor(level)} rounded-lg p-4 text-center`}>
              <div className="text-3xl font-bold">{count}</div>
              <div className="text-sm opacity-80">{level} Offices</div>
            </div>
          );
        })}
      </div>

      {/* Loading State */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      ) : (
        /* Office List by Level */
        <div className="space-y-6">
          {['National', 'State', 'Local'].map(level => {
            const levelOffices = groupedOffices[level] || [];
            if (levelOffices.length === 0 && filterLevel !== 'all' && filterLevel !== level) return null;
            
            return (
              <div key={level} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                {/* Level Header */}
                <div className={`px-6 py-4 border-b border-gray-200 flex items-center gap-3 ${getLevelColor(level)}`}>
                  {getLevelIcon(level)}
                  <h2 className="font-semibold">{level} Offices</h2>
                  <span className="ml-auto text-sm opacity-80">{levelOffices.length} offices</span>
                </div>
                
                {/* Office Table */}
                {levelOffices.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Position</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Representatives</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sort Order</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {levelOffices.map(office => (
                          <tr key={office.office_id} className="hover:bg-gray-50" data-testid={`office-row-${office.office_id}`}>
                            <td className="px-6 py-4">
                              <div className="font-medium text-gray-900">{office.position_title}</div>
                              {office.description && (
                                <div className="text-sm text-gray-500 truncate max-w-xs">{office.description}</div>
                              )}
                            </td>
                            <td className="px-6 py-4 text-gray-600">{office.category}</td>
                            <td className="px-6 py-4">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                office.representative_count > 0 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                              }`}>
                                {office.representative_count || 0} running
                              </span>
                            </td>
                            <td className="px-6 py-4 text-gray-600">{office.sort_order || 0}</td>
                            <td className="px-6 py-4 text-right">
                              <div className="flex items-center justify-end gap-2">
                                <button
                                  onClick={() => openEditModal(office)}
                                  className="p-2 text-gray-500 hover:text-primary hover:bg-primary/10 rounded-lg transition-colors"
                                  title="Edit office"
                                  data-testid={`edit-office-${office.office_id}`}
                                >
                                  <MdEdit className="text-lg" />
                                </button>
                                <button
                                  onClick={() => handleDelete(office)}
                                  className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                  title="Delete office"
                                  data-testid={`delete-office-${office.office_id}`}
                                >
                                  <MdDelete className="text-lg" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="px-6 py-8 text-center text-gray-500">
                    No {level.toLowerCase()} offices found
                    {searchQuery && ' matching your search'}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingOffice ? 'Edit Office' : 'Create New Office'}
              </h2>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-600">
                <MdClose className="text-2xl" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Level */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Level <span className="text-red-500">*</span>
                </label>
                <select
                  name="level"
                  value={formData.level}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                  data-testid="office-form-level"
                >
                  <option value="National">National</option>
                  <option value="State">State</option>
                  <option value="Local">Local</option>
                </select>
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  placeholder="e.g., Executive, Legislative, Judicial"
                  maxLength={100}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                  data-testid="office-form-category"
                />
              </div>

              {/* Position Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Position Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="position_title"
                  value={formData.position_title}
                  onChange={handleInputChange}
                  placeholder="e.g., Governor, U.S. Senator, Mayor"
                  maxLength={200}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                  data-testid="office-form-title"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Brief description of this office..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary resize-none"
                  data-testid="office-form-description"
                />
              </div>

              {/* Sort Order */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sort Order
                </label>
                <input
                  type="number"
                  name="sort_order"
                  value={formData.sort_order}
                  onChange={handleInputChange}
                  min={0}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                  data-testid="office-form-sort"
                />
                <p className="text-xs text-gray-500 mt-1">Lower numbers appear first within the same level</p>
              </div>

              {/* Error Message */}
              {error && showModal && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {error}
                </div>
              )}

              {/* Buttons */}
              <div className="flex gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50"
                  data-testid="office-form-submit"
                >
                  {submitting ? 'Saving...' : (editingOffice ? 'Update Office' : 'Create Office')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminOffices;
